import {
  $dt,
  definePreset,
  palette,
  updatePreset,
  updatePrimaryPalette,
  updateSurfacePalette,
  usePreset,
} from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import type { AuraBaseDesignTokens } from '@primeuix/themes/aura/base';
import type { PaletteDesignToken, Preset } from '@primeuix/themes/types';

/**
 * Application theme preset built on top of Aura.
 *
 * Token layers used in this file:
 * - primitive: raw palettes (indigo, zinc, ...)
 * - semantic: global UI intent (primary, surface, focus ring, form field)
 * - components: targeted component-level overrides (use sparingly)
 */
const appThemeOverrides = {
  // primitive: {
  //   indigo: {
  //     50: '#eef2ff',
  //     100: '#e0e7ff',
  //     200: '#c7d2fe',
  //     300: '#a5b4fc',
  //     400: '#818cf8',
  //     500: '#6366f1',
  //     600: '#4f46e5',
  //     700: '#4338ca',
  //     800: '#3730a3',
  //     900: '#312e81',
  //     950: '#1e1b4b',
  //   },
  // },
  // semantic: {
  //   primary: {
  //     50: '{indigo.50}',
  //     100: '{indigo.100}',
  //     200: '{indigo.200}',
  //     300: '{indigo.300}',
  //     400: '{indigo.400}',
  //     500: '{indigo.500}',
  //     600: '{indigo.600}',
  //     700: '{indigo.700}',
  //     800: '{indigo.800}',
  //     900: '{indigo.900}',
  //     950: '{indigo.950}',
  //   },
  //   focusRing: {
  //     width: '2px',
  //     style: 'solid',
  //     color: '{primary.500}',
  //     offset: '2px',
  //     shadow: 'none',
  //   },
  //   formField: {
  //     borderRadius: '0.5rem',
  //   },
  //   colorScheme: {
  //     light: {
  //       surface: {
  //         0: '#ffffff',
  //         50: '{zinc.50}',
  //         100: '{zinc.100}',
  //         200: '{zinc.200}',
  //         300: '{zinc.300}',
  //         400: '{zinc.400}',
  //         500: '{zinc.500}',
  //         600: '{zinc.600}',
  //         700: '{zinc.700}',
  //         800: '{zinc.800}',
  //         900: '{zinc.900}',
  //         950: '{zinc.950}',
  //       },
  //       formField: {
  //         hoverBorderColor: '{primary.color}',
  //         focusBorderColor: '{primary.color}',
  //       },
  //     },
  //     dark: {
  //       surface: {
  //         0: '#ffffff',
  //         50: '{slate.50}',
  //         100: '{slate.100}',
  //         200: '{slate.200}',
  //         300: '{slate.300}',
  //         400: '{slate.400}',
  //         500: '{slate.500}',
  //         600: '{slate.600}',
  //         700: '{slate.700}',
  //         800: '{slate.800}',
  //         900: '{slate.900}',
  //         950: '{slate.950}',
  //       },
  //       formField: {
  //         hoverBorderColor: '{primary.color}',
  //         focusBorderColor: '{primary.color}',
  //       },
  //     },
  //   },
  // },
  // components: {
  //   button: {
  //     root: {
  //       borderRadius: '0.5rem',
  //     },
  //   },
  // },
  // extend: {
  //   app: {
  //     chart: {
  //       accent: '{primary.500}',
  //     },
  //     onboarding: {
  //       heroBackground: '{surface.100}',
  //     },
  //   },
  // },
  // css: ({ dt }) => `
  //   .app-focus-ring {
  //     outline: ${dt('focus.ring.width')} ${dt('focus.ring.style')} ${dt('focus.ring.color')};
  //     outline-offset: ${dt('focus.ring.offset')};
  //     box-shadow: ${dt('focus.ring.shadow')};
  //   }
  // `,
} satisfies Preset<AuraBaseDesignTokens>;

export const appThemePreset = definePreset(Aura, appThemeOverrides);

/**
 * PrimeNG theme options for this app.
 *
 * - prefix: CSS variable prefix (`--p-*`)
 * - darkModeSelector: class-based dark mode toggle
 * - cssLayer: places PrimeNG styles in a named layer for predictable specificity
 */
export const appThemeOptions = {
  // prefix: 'p',
  darkModeSelector: '.app-dark',
  cssLayer: {
    name: 'primeng',
    order: 'theme, base, primeng',
  },
} as const;

/**
 * Combined `theme` object consumed by `providePrimeNG`.
 */
export const appPrimeNgTheme = {
  preset: appThemePreset,
  options: appThemeOptions,
} as const;

/**
 * Example noir variant where primary colors are mapped from surface tones.
 *
 * This is exported for reference and can be enabled at runtime via `usePreset`.
 */
export const noirPresetExample = definePreset(Aura, {
  // semantic: {
  //   colorScheme: {
  //     light: {
  //       primary: {
  //         color: '{surface.900}',
  //         contrastColor: '{surface.0}',
  //         hoverColor: '{surface.800}',
  //         activeColor: '{surface.700}',
  //       },
  //     },
  //     dark: {
  //       primary: {
  //         color: '{surface.100}',
  //         contrastColor: '{surface.950}',
  //         hoverColor: '{surface.200}',
  //         activeColor: '{surface.300}',
  //       },
  //     },
  //   },
  // },
});

/**
 * Runtime helper: merge a partial preset into the active preset.
 * Equivalent to PrimeNG docs `updatePreset`.
 */
export function mergeThemePreset(partialPreset: Preset): Preset {
  return updatePreset(partialPreset);
}

/**
 * Runtime helper: update only the primary palette.
 * Equivalent to docs `updatePrimaryPalette` shorthand.
 */
export function applyPrimaryPalette(primaryPalette: PaletteDesignToken): Preset {
  return updatePrimaryPalette(primaryPalette);
}

/**
 * Runtime helper: update only the surface palette.
 * Equivalent to docs `updateSurfacePalette` shorthand.
 */
export function applySurfacePalette(surfacePalette: PaletteDesignToken): Preset {
  return updateSurfacePalette(surfacePalette);
}

/**
 * Runtime helper: replace the active preset completely.
 * Equivalent to docs `usePreset`.
 */
export function replaceThemePreset(nextPreset: Preset): Preset {
  return usePreset(nextPreset);
}

/**
 * Runtime helper: generate a color scale from a seed color.
 *
 * Prime UI returns either a palette object or a string fallback.
 * The fallback branch keeps this function fully type-safe.
 */
export function createPaletteFromColor(seedColor: string): PaletteDesignToken {
  const generated = palette(seedColor);

  if (typeof generated === 'string') {
    return {};
  }

  return generated;
}

/**
 * Runtime helper: inspect the resolved token value.
 * Equivalent to docs `$dt` utility usage.
 */
export function readTokenValue(tokenPath: string): unknown {
  return $dt(tokenPath).value;
}
